import { createReducer, on } from '@ngrx/store';
import type {
  IBiomapHabitatCompensationResponse,
  IBiomapMsaCompensationResponse,
} from '../../../interfaces/strategic-view/IBiomapCompensation';
import type {
  BiomapInterventionGeometryTarget,
  IBiomapHabitatInterventionResponse,
  IBiomapInterventionAvailability,
  IBiomapInterventionSession,
  IBiomapMsaInterventionResponse,
} from '../../../interfaces/strategic-view/IBiomapInterventionAssessment';
import type {
  IBiomapMsaScenarioAnalysisResponse,
  IBiomapScenarioSession,
} from '../../../interfaces/strategic-view/IBiomapScenarioAnalysis';
import type {
  BiomapStrategicChangeProduct,
  IBiomapStrategicChangeResponse,
} from '../../../interfaces/strategic-view/IBiomapStrategicChange';
import {
  IStrategicComparisonResponse,
  IStrategicOverviewResponse,
  StrategicComparisonMode,
} from '../../../interfaces/strategic-view/IStrategicView';
import * as StrategicViewActions from '../../actions/strategic-view/strategic-view.actions';

const INTERVENTION_GEOMETRY_TARGET: BiomapInterventionGeometryTarget = 'intervention';
const COMPENSATION_GEOMETRY_TARGET: BiomapInterventionGeometryTarget = 'compensation';

export interface StrategicViewState {
  overview: {
    entities: Record<string, IStrategicOverviewResponse>;
    loadingLayerUUID: string | null;
    errors: Record<string, any>;
  };
  comparison: {
    entities: Record<string, IStrategicComparisonResponse>;
    loadingKey: string | null;
    errors: Record<string, any>;
  };
  biomapChange: {
    entities: Record<string, IBiomapStrategicChangeResponse>;
    loading: Record<string, boolean>;
    errors: Record<string, string | null>;
  };
  biomapScenario: {
    session: IBiomapScenarioSession;
    analysis: {
      result: IBiomapMsaScenarioAnalysisResponse | null;
      loading: boolean;
      error: string | null;
    };
  };
  biomapIntervention: {
    session: IBiomapInterventionSession;
    availability: IBiomapInterventionAvailability;
    geometryImport: {
      loading: boolean;
      error: string | null;
    };
    compensationGeometryImport: {
      loading: boolean;
      error: string | null;
    };
    habitat: {
      result: IBiomapHabitatInterventionResponse | null;
      loading: boolean;
      error: string | null;
    };
    msa: {
      result: IBiomapMsaInterventionResponse | null;
      loading: boolean;
      error: string | null;
    };
    compensation: {
      habitat: {
        result: IBiomapHabitatCompensationResponse | null;
        loading: boolean;
        error: string | null;
      };
      msa: {
        result: IBiomapMsaCompensationResponse | null;
        loading: boolean;
        error: string | null;
      };
    };
  };
}

export const initialState: StrategicViewState = {
  overview: {
    entities: {},
    loadingLayerUUID: null,
    errors: {},
  },
  comparison: {
    entities: {},
    loadingKey: null,
    errors: {},
  },
  biomapChange: {
    entities: {},
    loading: {},
    errors: {},
  },
  biomapScenario: {
    session: {
      active: false,
      rasterLayerUuid: null,
      catalog: null,
      catalogLoading: false,
      catalogError: null,
      baselineSourceUuid: null,
      scenario: null,
      targetYear: null,
      comparisonScenarios: [],
    },
    analysis: {
      result: null,
      loading: false,
      error: null,
    },
  },
  biomapIntervention: {
    session: {
      active: false,
      geometry: null,
      contextBufferM: null,
      selectedProducts: {
        habitat: false,
        msa: false,
      },
      habitatClassification: 'l1',
      compensationEnabled: false,
      compensationGeometry: null,
      geometryTarget: INTERVENTION_GEOMETRY_TARGET,
      drawing: false,
    },
    availability: {
      habitat: false,
      habitatL1: false,
      habitatL2: false,
      msa: false,
    },
    geometryImport: {
      loading: false,
      error: null,
    },
    compensationGeometryImport: {
      loading: false,
      error: null,
    },
    habitat: {
      result: null,
      loading: false,
      error: null,
    },
    msa: {
      result: null,
      loading: false,
      error: null,
    },
    compensation: {
      habitat: {
        result: null,
        loading: false,
        error: null,
      },
      msa: {
        result: null,
        loading: false,
        error: null,
      },
    },
  },
};

export function buildStrategicComparisonKey(
  projectUuid: string,
  rasterLayerUuid: string,
  sourceAUuid: string,
  sourceBUuid: string,
  comparisonMode: StrategicComparisonMode = 'difference',
): string {
  return [projectUuid, rasterLayerUuid, sourceAUuid, sourceBUuid, comparisonMode].join('|');
}

export function buildBiomapStrategicChangeKey(
  projectUuid: string,
  rasterLayerUuid: string,
  product: BiomapStrategicChangeProduct,
  sourceBeforeUuid: string,
  sourceAfterUuid: string,
): string {
  return [
    'biomap-change',
    projectUuid,
    rasterLayerUuid,
    product,
    sourceBeforeUuid,
    sourceAfterUuid,
  ].join('|');
}

export const strategicViewReducer = createReducer(
  initialState,
  on(StrategicViewActions.loadStrategicRasterOverview, (state, { rasterLayerUuid }) => ({
    ...state,
    overview: {
      ...state.overview,
      loadingLayerUUID: rasterLayerUuid,
      errors: {
        ...state.overview.errors,
        [rasterLayerUuid]: null,
      },
    },
  })),
  on(StrategicViewActions.loadStrategicRasterOverviewSuccess, (state, { rasterLayerUuid, overview }) => ({
    ...state,
    overview: {
      ...state.overview,
      entities: {
        ...state.overview.entities,
        [rasterLayerUuid]: overview,
      },
      loadingLayerUUID: state.overview.loadingLayerUUID === rasterLayerUuid ? null : state.overview.loadingLayerUUID,
      errors: {
        ...state.overview.errors,
        [rasterLayerUuid]: null,
      },
    },
  })),
  on(StrategicViewActions.loadStrategicRasterOverviewFailure, (state, { rasterLayerUuid, error }) => ({
    ...state,
    overview: {
      ...state.overview,
      loadingLayerUUID: state.overview.loadingLayerUUID === rasterLayerUuid ? null : state.overview.loadingLayerUUID,
      errors: {
        ...state.overview.errors,
        [rasterLayerUuid]: error,
      },
    },
  })),
  on(StrategicViewActions.loadStrategicRasterComparison, (state, action) => {
    const comparisonKey = buildStrategicComparisonKey(
      action.projectUuid,
      action.rasterLayerUuid,
      action.sourceAUuid,
      action.sourceBUuid,
      action.comparisonMode ?? 'difference',
    );

    return {
      ...state,
      comparison: {
        ...state.comparison,
        loadingKey: comparisonKey,
        errors: {
          ...state.comparison.errors,
          [comparisonKey]: null,
        },
      },
    };
  }),
  on(StrategicViewActions.loadStrategicRasterComparisonSuccess, (state, { comparisonKey, comparison }) => ({
    ...state,
    comparison: {
      ...state.comparison,
      entities: {
        ...state.comparison.entities,
        [comparisonKey]: comparison,
      },
      loadingKey: state.comparison.loadingKey === comparisonKey ? null : state.comparison.loadingKey,
      errors: {
        ...state.comparison.errors,
        [comparisonKey]: null,
      },
    },
  })),
  on(StrategicViewActions.loadStrategicRasterComparisonFailure, (state, { comparisonKey, error }) => ({
    ...state,
    comparison: {
      ...state.comparison,
      loadingKey: state.comparison.loadingKey === comparisonKey ? null : state.comparison.loadingKey,
      errors: {
        ...state.comparison.errors,
        [comparisonKey]: error,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapStrategicChange, (state, action) => {
    const changeKey = buildBiomapStrategicChangeKey(
      action.projectUuid,
      action.rasterLayerUuid,
      action.product,
      action.sourceBeforeUuid,
      action.sourceAfterUuid,
    );

    return {
      ...state,
      biomapChange: {
        ...state.biomapChange,
        loading: {
          ...state.biomapChange.loading,
          [changeKey]: true,
        },
        errors: {
          ...state.biomapChange.errors,
          [changeKey]: null,
        },
      },
    };
  }),
  on(StrategicViewActions.loadBiomapStrategicChangeSuccess, (state, { changeKey, change }) => ({
    ...state,
    biomapChange: {
      entities: {
        ...state.biomapChange.entities,
        [changeKey]: change,
      },
      loading: {
        ...state.biomapChange.loading,
        [changeKey]: false,
      },
      errors: {
        ...state.biomapChange.errors,
        [changeKey]: null,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapStrategicChangeFailure, (state, { changeKey, error }) => ({
    ...state,
    biomapChange: {
      ...state.biomapChange,
      loading: {
        ...state.biomapChange.loading,
        [changeKey]: false,
      },
      errors: {
        ...state.biomapChange.errors,
        [changeKey]: error,
      },
    },
  })),
  on(
    StrategicViewActions.openBiomapInterventionAssessment,
    (state, { availability, selectedProducts, habitatClassification }) => ({
      ...state,
      biomapIntervention: {
        session: {
          active: true,
          geometry: null,
          contextBufferM: null,
          selectedProducts,
          habitatClassification,
          compensationEnabled: false,
          compensationGeometry: null,
          geometryTarget: INTERVENTION_GEOMETRY_TARGET,
          drawing: false,
        },
        availability,
        geometryImport: {
          loading: false,
          error: null,
        },
        compensationGeometryImport: {
          loading: false,
          error: null,
        },
        habitat: {
          result: null,
          loading: false,
          error: null,
        },
        msa: {
          result: null,
          loading: false,
          error: null,
        },
        compensation: {
          habitat: { result: null, loading: false, error: null },
          msa: { result: null, loading: false, error: null },
        },
      },
    }),
  ),
  on(StrategicViewActions.closeBiomapInterventionAssessment, (state) => ({
    ...state,
    biomapIntervention: initialState.biomapIntervention,
  })),
  on(
    StrategicViewActions.updateBiomapInterventionAvailability,
    (state, { availability, selectedProducts, habitatClassification }) => ({
      ...state,
      biomapIntervention: {
        ...state.biomapIntervention,
        session: {
          ...state.biomapIntervention.session,
          selectedProducts,
          habitatClassification,
          geometryTarget: INTERVENTION_GEOMETRY_TARGET,
          drawing: false,
        },
        availability,
        habitat: {
          result: null,
          loading: false,
          error: null,
        },
        msa: {
          result: null,
          loading: false,
          error: null,
        },
        compensation: {
          habitat: { result: null, loading: false, error: null },
          msa: { result: null, loading: false, error: null },
        },
      },
    }),
  ),
  on(StrategicViewActions.setBiomapInterventionGeometry, (state, { geometry }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        geometry,
      },
      geometryImport: {
        loading: false,
        error: null,
      },
      habitat: {
        ...state.biomapIntervention.habitat,
        result: null,
        error: null,
      },
      msa: {
        ...state.biomapIntervention.msa,
        result: null,
        error: null,
      },
      compensation: {
        habitat: { ...state.biomapIntervention.compensation.habitat, result: null, error: null },
        msa: { ...state.biomapIntervention.compensation.msa, result: null, error: null },
      },
    },
  })),
  on(
    StrategicViewActions.setBiomapInterventionContextBuffer,
    (state, { contextBufferM }) => ({
      ...state,
      biomapIntervention: {
        ...state.biomapIntervention,
        session: {
          ...state.biomapIntervention.session,
          contextBufferM,
        },
        habitat: {
          ...state.biomapIntervention.habitat,
          result: null,
          error: null,
        },
        msa: {
          ...state.biomapIntervention.msa,
          result: null,
          error: null,
        },
      },
    }),
  ),
  on(
    StrategicViewActions.setBiomapInterventionProductSelection,
    (state, { product, selected }) => {
      const nextSession = {
        ...state.biomapIntervention.session,
        selectedProducts: {
          ...state.biomapIntervention.session.selectedProducts,
          [product]: selected,
        },
      };

      if (product === 'habitat') {
        return {
          ...state,
          biomapIntervention: {
            ...state.biomapIntervention,
            session: nextSession,
            habitat: selected
              ? state.biomapIntervention.habitat
              : { result: null, loading: false, error: null },
            compensation: {
              ...state.biomapIntervention.compensation,
              habitat: selected
                ? state.biomapIntervention.compensation.habitat
                : { result: null, loading: false, error: null },
            },
          },
        };
      }

      return {
        ...state,
        biomapIntervention: {
          ...state.biomapIntervention,
          session: nextSession,
          msa: selected
            ? state.biomapIntervention.msa
            : { result: null, loading: false, error: null },
          compensation: {
            ...state.biomapIntervention.compensation,
            msa: selected
              ? state.biomapIntervention.compensation.msa
              : { result: null, loading: false, error: null },
          },
        },
      };
    },
  ),
  on(
    StrategicViewActions.setBiomapInterventionHabitatClassification,
    (state, { habitatClassification }) => ({
      ...state,
      biomapIntervention: {
        ...state.biomapIntervention,
        session: {
          ...state.biomapIntervention.session,
          habitatClassification,
        },
        habitat: {
          result: null,
          loading: false,
          error: null,
        },
        compensation: {
          ...state.biomapIntervention.compensation,
          habitat: { result: null, loading: false, error: null },
        },
      },
    }),
  ),
  on(StrategicViewActions.setBiomapInterventionDrawing, (state, { drawing }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        drawing,
      },
    },
  })),
  on(StrategicViewActions.setBiomapInterventionGeometryTarget, (state, { target }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        geometryTarget: target,
      },
    },
  })),
  on(StrategicViewActions.setBiomapCompensationEnabled, (state, { enabled }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        compensationEnabled: enabled,
        geometryTarget: enabled
          ? state.biomapIntervention.session.geometryTarget
          : INTERVENTION_GEOMETRY_TARGET,
        drawing: enabled ? state.biomapIntervention.session.drawing : false,
      },
      compensationGeometryImport: enabled
        ? state.biomapIntervention.compensationGeometryImport
        : { loading: false, error: null },
      compensation: enabled
        ? state.biomapIntervention.compensation
        : {
            habitat: { result: null, loading: false, error: null },
            msa: { result: null, loading: false, error: null },
          },
    },
  })),
  on(StrategicViewActions.setBiomapCompensationGeometry, (state, { geometry }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        compensationGeometry: geometry,
      },
      compensationGeometryImport: { loading: false, error: null },
      compensation: {
        habitat: { ...state.biomapIntervention.compensation.habitat, result: null, error: null },
        msa: { ...state.biomapIntervention.compensation.msa, result: null, error: null },
      },
    },
  })),
  on(StrategicViewActions.uploadBiomapInterventionShapefile, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      geometryImport: {
        loading: true,
        error: null,
      },
    },
  })),
  on(
    StrategicViewActions.uploadBiomapInterventionShapefileSuccess,
    (state, { geometry }) => ({
      ...state,
      biomapIntervention: {
        ...state.biomapIntervention,
        session: {
          ...state.biomapIntervention.session,
          geometry,
          geometryTarget: INTERVENTION_GEOMETRY_TARGET,
          drawing: false,
        },
        geometryImport: {
          loading: false,
          error: null,
        },
        habitat: {
          ...state.biomapIntervention.habitat,
          result: null,
          error: null,
        },
        msa: {
          ...state.biomapIntervention.msa,
          result: null,
          error: null,
        },
        compensation: {
          habitat: { ...state.biomapIntervention.compensation.habitat, result: null, error: null },
          msa: { ...state.biomapIntervention.compensation.msa, result: null, error: null },
        },
      },
    }),
  ),
  on(StrategicViewActions.uploadBiomapInterventionShapefileFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      geometryImport: {
        loading: false,
        error,
      },
    },
  })),
  on(StrategicViewActions.uploadBiomapCompensationShapefile, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensationGeometryImport: { loading: true, error: null },
    },
  })),
  on(StrategicViewActions.uploadBiomapCompensationShapefileSuccess, (state, { geometry }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      session: {
        ...state.biomapIntervention.session,
        compensationGeometry: geometry,
        geometryTarget: COMPENSATION_GEOMETRY_TARGET,
        drawing: false,
      },
      compensationGeometryImport: { loading: false, error: null },
      compensation: {
        habitat: { ...state.biomapIntervention.compensation.habitat, result: null, error: null },
        msa: { ...state.biomapIntervention.compensation.msa, result: null, error: null },
      },
    },
  })),
  on(StrategicViewActions.uploadBiomapCompensationShapefileFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensationGeometryImport: { loading: false, error },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatIntervention, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      habitat: {
        result: null,
        loading: true,
        error: null,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatInterventionSuccess, (state, { result }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      habitat: {
        result,
        loading: false,
        error: null,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatInterventionFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      habitat: {
        result: null,
        loading: false,
        error,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaIntervention, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      msa: {
        result: null,
        loading: true,
        error: null,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaInterventionSuccess, (state, { result }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      msa: {
        result,
        loading: false,
        error: null,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaInterventionFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      msa: {
        result: null,
        loading: false,
        error,
      },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatCompensation, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        habitat: { result: null, loading: true, error: null },
      },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatCompensationSuccess, (state, { result }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        habitat: { result, loading: false, error: null },
      },
    },
  })),
  on(StrategicViewActions.loadBiomapHabitatCompensationFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        habitat: { result: null, loading: false, error },
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaCompensation, (state) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        msa: { result: null, loading: true, error: null },
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaCompensationSuccess, (state, { result }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        msa: { result, loading: false, error: null },
      },
    },
  })),
  on(StrategicViewActions.loadBiomapMsaCompensationFailure, (state, { error }) => ({
    ...state,
    biomapIntervention: {
      ...state.biomapIntervention,
      compensation: {
        ...state.biomapIntervention.compensation,
        msa: { result: null, loading: false, error },
      },
    },
  })),

  on(StrategicViewActions.openBiomapScenarioAnalysis, (state, { rasterLayerUuid }) => ({
    ...state,
    biomapScenario: {
      session: {
        active: true,
        rasterLayerUuid,
        catalog: null,
        catalogLoading: true,
        catalogError: null,
        baselineSourceUuid: null,
        scenario: null,
        targetYear: null,
        comparisonScenarios: [],
      },
      analysis: { result: null, loading: false, error: null },
    },
  })),
  on(StrategicViewActions.loadBiomapScenarioCatalogSuccess, (state, { catalog, selection }) => ({
    ...state,
    biomapScenario: {
      session: {
        ...state.biomapScenario.session,
        active: true,
        rasterLayerUuid: catalog.rasterLayerUuid,
        catalog,
        catalogLoading: false,
        catalogError: null,
        ...selection,
      },
      analysis: { result: null, loading: false, error: null },
    },
  })),
  on(StrategicViewActions.loadBiomapScenarioCatalogFailure, (state, { error }) => ({
    ...state,
    biomapScenario: {
      ...state.biomapScenario,
      session: {
        ...state.biomapScenario.session,
        catalogLoading: false,
        catalogError: error,
      },
    },
  })),
  on(StrategicViewActions.configureBiomapScenarioAnalysis, (state, { selection }) => ({
    ...state,
    biomapScenario: {
      session: {
        ...state.biomapScenario.session,
        ...selection,
      },
      analysis: { result: null, loading: false, error: null },
    },
  })),
  on(StrategicViewActions.runBiomapScenarioAnalysis, (state) => ({
    ...state,
    biomapScenario: {
      ...state.biomapScenario,
      analysis: { result: null, loading: true, error: null },
    },
  })),
  on(StrategicViewActions.runBiomapScenarioAnalysisSuccess, (state, { result }) => ({
    ...state,
    biomapScenario: {
      ...state.biomapScenario,
      analysis: { result, loading: false, error: null },
    },
  })),
  on(StrategicViewActions.runBiomapScenarioAnalysisFailure, (state, { error }) => ({
    ...state,
    biomapScenario: {
      ...state.biomapScenario,
      analysis: { result: null, loading: false, error },
    },
  })),
  on(StrategicViewActions.closeBiomapScenarioAnalysis, (state) => ({
    ...state,
    biomapScenario: initialState.biomapScenario,
  })),
  on(StrategicViewActions.clearStrategicViewState, () => initialState),
);
